import React, { useState, useEffect } from 'react';
import API from '../api';
import { useAuth } from '../contexts/AuthContext';
import { HardHat, FileText, Users, TrendingUp, ArrowRight, DollarSign, BarChart3, PieChart, Target, ClipboardList, Trophy, Calendar, AlertTriangle, ArrowDown, ArrowUp, Scale } from 'lucide-react';

export default function DashboardPage({ onNavigate }) {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [financials, setFinancials] = useState(null);

  useEffect(() => {
    API.get('/api/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    API.get('/api/dashboard/financials').then(r => setFinancials(r.data)).catch(() => {});
  }, []);

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  const statoLabels = {
    da_avviare: 'Da Avviare', in_corso: 'In Corso', sospeso: 'Sospeso', completato: 'Completato',
    bozza: 'Bozza', pronto: 'Pronto', inviato: 'Inviato', approvato: 'Approvato', validato: 'Validato',
  };
  const statoColors = {
    da_avviare: 'bg-yellow-100 text-yellow-800', in_corso: 'bg-green-100 text-green-800',
    sospeso: 'bg-red-100 text-red-800', completato: 'bg-blue-100 text-blue-800',
    bozza: 'bg-slate-100 text-slate-700', approvato: 'bg-green-100 text-green-700', validato: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-base text-slate-500 mt-1.5">Benvenuto, <span className="font-semibold text-slate-700">{user?.nome || 'Utente'}</span></p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard icon={HardHat} label="Cantieri Attivi" value={stats?.cantieri_attivi ?? '-'} color="bg-primary" onClick={() => onNavigate('cantieri')} />
        <KpiCard icon={FileText} label="Preventivi Bozza" value={stats?.preventivi_bozza ?? '-'} color="bg-accent-info" onClick={() => onNavigate('preventivi')} />
        <KpiCard icon={Users} label="Clienti" value={stats?.clienti_totali ?? '-'} color="bg-accent-success" onClick={() => onNavigate('clienti')} />
        <KpiCard icon={TrendingUp} label="Pratiche Aperte" value={stats?.pratiche_aperte ?? '-'} color="bg-accent-warning" onClick={() => onNavigate('pratiche')} />
      </div>

      {/* Contabilità */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6 shadow-sm" data-testid="contabilita-section">
        <h2 className="text-xl font-extrabold mb-5 flex items-center gap-2.5"><BarChart3 size={22} className="text-primary" /> Contabilita Aziendale</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <FinanceCard label="Fatturato Confermato" value={fmt(stats?.fatturato_confermato)} sub={`${stats?.prev_approvati || 0} preventivi`} highlight />
          <FinanceCard label="Imponibile" value={fmt(stats?.fatturato_imponibile)} sub="Senza IVA" />
          <FinanceCard label="Pipeline Bozze" value={fmt(stats?.pipeline_valore)} sub={`${stats?.preventivi_bozza || 0} in bozza`} muted />
          <FinanceCard label="Lavori Eseguiti" value={fmt(stats?.lavori_eseguiti)} sub="Da SAL approvati" success />
          <FinanceCard label="Tasso Conversione" value={`${stats?.tasso_conversione || 0}%`} sub={`${stats?.prev_approvati || 0} / ${stats?.prev_totali || 0}`} />
        </div>
      </div>

      {/* Bilancio SDI (FattureInCloud) */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6 shadow-sm" data-testid="bilancio-sdi-section">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-extrabold flex items-center gap-2.5"><Scale size={22} className="text-primary" /> Bilancio Fatture (SDI)</h2>
          <button onClick={() => onNavigate('fatture-attive')} className="text-xs text-primary font-semibold hover:underline" data-testid="bilancio-vedi-fatture">Vedi tutte →</button>
        </div>
        {!financials ? (
          <p className="text-sm text-slate-400 py-4" data-testid="bilancio-empty">Caricamento bilancio…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['mese', 'trimestre', 'anno'].map((k) => (
              <BalanceCard key={k} period={k} data={financials.periods?.[k]} fmt={fmt} />
            ))}
          </div>
        )}
      </div>

      {/* Scadenze imminenti */}
      {financials && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <ScadenzaCard
            title="Scadute"
            icon={AlertTriangle}
            color="bg-red-50 border-red-200 text-red-700"
            items={financials.scadenze?.scadute || []}
            total={financials.scadenze?.tot_scadute || 0}
            fmt={fmt}
            tipo="passiva"
            testId="scadute-card"
          />
          <ScadenzaCard
            title="In Scadenza (30gg) – Da Pagare"
            icon={Calendar}
            color="bg-amber-50 border-amber-200 text-amber-800"
            items={financials.scadenze?.in_arrivo_passive || []}
            total={financials.scadenze?.tot_passive_30gg || 0}
            fmt={fmt}
            tipo="passiva"
            testId="scadenze-passive-card"
          />
          <ScadenzaCard
            title="Incassi Attesi (30gg)"
            icon={ArrowDown}
            color="bg-emerald-50 border-emerald-200 text-emerald-700"
            items={financials.scadenze?.in_arrivo_attive || []}
            total={financials.scadenze?.tot_attive_30gg || 0}
            fmt={fmt}
            tipo="attiva"
            testId="scadenze-attive-card"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Top Clienti per Fatturato */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="top-clienti">
          <h3 className="text-base font-bold mb-4 flex items-center gap-2"><Trophy size={18} className="text-primary" /> Top Clienti per Fatturato</h3>
          {stats?.top_clienti?.length > 0 ? stats.top_clienti.map((c, i) => {
            const maxFatt = stats.top_clienti[0]?.fatturato || 1;
            const pct = Math.round(c.fatturato / maxFatt * 100);
            return (
              <div key={c.cliente_id || c.nome || i} className="mb-3 last:mb-0" data-testid={`top-client-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary font-black text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <p className="text-sm font-semibold truncate">{c.nome}</p>
                  </div>
                  <p className="font-mono font-bold text-sm text-primary flex-shrink-0 ml-2">{fmt(c.fatturato)}</p>
                </div>
                <div className="ml-7">
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${pct}%` }}></div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">{c.preventivi} preventivi approvati</p>
                </div>
              </div>
            );
          }) : <p className="text-sm text-slate-400 py-4">Nessun dato ancora</p>}
        </div>

        {/* Avanzamento Cantieri */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="avanzamento-cantieri">
          <h3 className="text-base font-bold mb-4 flex items-center gap-2"><ClipboardList size={18} className="text-accent-success" /> Avanzamento Cantieri</h3>
          {stats?.cantieri_avanzamento?.length > 0 ? stats.cantieri_avanzamento.map((c, i) => (
            <div key={c.id || c.codice || i} className="mb-3 last:mb-0">
              <div className="flex items-center justify-between mb-1">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{c.titolo || c.codice}</p>
                  <p className="text-[10px] text-slate-400">{c.codice}{c.ultimo_sal ? ` - ${c.ultimo_sal}` : ''}</p>
                </div>
                <span className="font-mono font-bold text-sm flex-shrink-0 ml-2">{c.percentuale || 0}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div className={`rounded-full h-2.5 transition-all ${c.percentuale >= 80 ? 'bg-accent-success' : c.percentuale >= 40 ? 'bg-primary' : 'bg-accent-warning'}`}
                  style={{ width: `${Math.min(100, c.percentuale || 0)}%` }}></div>
              </div>
            </div>
          )) : <p className="text-sm text-slate-400 py-4">Nessun cantiere attivo</p>}
        </div>

        {/* Riepilogo numerico */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="riepilogo-numeri">
          <h3 className="text-base font-bold mb-4 flex items-center gap-2"><PieChart size={18} className="text-accent-info" /> Riepilogo</h3>
          <div className="space-y-3">
            <NumRow label="Preventivi Totali" value={stats?.prev_totali || 0} />
            <NumRow label="Preventivi Approvati" value={stats?.prev_approvati || 0} color="text-accent-success" />
            <NumRow label="Preventivi in Bozza" value={stats?.preventivi_bozza || 0} color="text-slate-400" />
            <NumRow label="Cantieri Attivi" value={stats?.cantieri_attivi || 0} color="text-primary" />
            <NumRow label="Pratiche Aperte" value={stats?.pratiche_aperte || 0} color="text-accent-warning" />
            <NumRow label="Clienti Totali" value={stats?.clienti_totali || 0} />
            <div className="border-t border-slate-200 pt-2 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Totale Emesso</span>
                <span className="font-mono font-black text-primary">{fmt(stats?.totale_emesso)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold">Ultimi Cantieri</h3>
            <button onClick={() => onNavigate('cantieri')} className="text-sm text-primary font-semibold hover:underline">Vedi tutti</button>
          </div>
          {stats?.recent_cantieri?.length > 0 ? stats.recent_cantieri.map((c, i) => (
            <div key={c.id || c.codice_cantiere || i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{c.titolo || c.codice_cantiere}</p>
                <p className="text-xs text-slate-400">{c.codice_cantiere}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ${statoColors[c.stato] || 'bg-slate-100'}`}>
                {statoLabels[c.stato] || c.stato}
              </span>
            </div>
          )) : <p className="text-sm text-slate-400 py-4">Nessun cantiere ancora</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold">Ultimi Preventivi</h3>
            <button onClick={() => onNavigate('preventivi')} className="text-sm text-primary font-semibold hover:underline">Vedi tutti</button>
          </div>
          {stats?.recent_preventivi?.length > 0 ? stats.recent_preventivi.map((p, i) => (
            <div key={p.id || p.numero_preventivo || i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{p.titolo || p.numero_preventivo}</p>
                <p className="text-xs text-slate-400">{p.numero_preventivo}</p>
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${statoColors[p.stato] || 'bg-slate-100'}`}>
                  {statoLabels[p.stato] || p.stato}
                </span>
                {p.totale_finale && <p className="text-xs font-mono mt-1 text-slate-600">{fmt(p.totale_finale)}</p>}
              </div>
            </div>
          )) : <p className="text-sm text-slate-400 py-4">Nessun preventivo ancora</p>}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <button onClick={onClick} data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}
      className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 text-left hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200 group">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-9 h-9 sm:w-10 sm:h-10 ${color} rounded-md flex items-center justify-center`}>
          <Icon size={18} className="text-white" />
        </div>
        <ArrowRight size={14} className="text-slate-300 group-hover:text-primary transition-colors" />
      </div>
      <p className="text-xl sm:text-2xl font-black">{value}</p>
      <p className="text-xs sm:text-sm text-slate-500 font-medium">{label}</p>
    </button>
  );
}

function FinanceCard({ label, value, sub, highlight, muted, success }) {
  return (
    <div className={`rounded-md p-3 ${highlight ? 'bg-primary border border-primary/20' : success ? 'bg-green-50 border border-green-200' : muted ? 'bg-slate-50 border border-slate-200' : 'bg-white border border-slate-200'}`}>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg sm:text-xl font-black font-mono ${highlight ? 'text-primary' : success ? 'text-accent-success' : muted ? 'text-slate-400' : ''}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function NumRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`font-mono font-bold text-sm ${color || ''}`}>{value}</span>
    </div>
  );
}

function BalanceCard({ period, data, fmt }) {
  const labels = { mese: 'Mese Corrente', trimestre: 'Trimestre', anno: 'Anno' };
  const margine = data?.margine || 0;
  const positive = margine >= 0;
  return (
    <div className={`rounded-lg border p-4 ${positive ? 'bg-emerald-50/40 border-emerald-200' : 'bg-red-50/40 border-red-200'}`} data-testid={`balance-${period}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{labels[period]}</p>
        <span className="text-[10px] text-slate-400">{data?.from} → {data?.to}</span>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-600 flex items-center gap-1"><ArrowDown size={12} className="text-emerald-600" /> Attive</span>
          <span className="font-mono text-sm font-bold text-emerald-700">{fmt(data?.attive?.totale)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-600 flex items-center gap-1"><ArrowUp size={12} className="text-red-600" /> Passive</span>
          <span className="font-mono text-sm font-bold text-red-700">{fmt(data?.passive?.totale)}</span>
        </div>
        <div className="border-t border-slate-200 pt-2 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-700">Margine</span>
          <span className={`font-mono text-base font-black ${positive ? 'text-emerald-700' : 'text-red-700'}`} data-testid={`margine-${period}`}>{fmt(margine)}</span>
        </div>
        <p className="text-[10px] text-slate-400 text-right">
          {data?.attive?.count || 0} att. · {data?.passive?.count || 0} pass.
        </p>
      </div>
    </div>
  );
}

function ScadenzaCard({ title, icon: Icon, color, items, total, fmt, tipo, testId }) {
  return (
    <div className={`rounded-md border p-5 ${color}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold flex items-center gap-2"><Icon size={18} /> {title}</h3>
        <span className="text-xs font-bold opacity-70">{items.length}</span>
      </div>
      {items.length > 0 ? (
        <>
          <p className="font-mono font-black text-lg mb-3">{fmt(total)}</p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {items.slice(0, 8).map((it, i) => (
              <div key={it.id || i} className="flex items-center justify-between text-xs bg-white/60 rounded px-2 py-1.5">
                <div className="min-w-0">
                  <p className="font-semibold truncate text-slate-800">{tipo === 'attiva' ? it.cliente_nome : it.fornitore_nome}</p>
                  <p className="text-[10px] text-slate-500">N° {it.numero} · {it.data_scadenza}</p>
                </div>
                <span className="font-mono font-bold text-slate-700 flex-shrink-0 ml-2">{fmt(it.importo_totale)}</span>
              </div>
            ))}
            {items.length > 8 && <p className="text-[10px] text-center pt-1 opacity-60">+ altri {items.length - 8} record</p>}
          </div>
        </>
      ) : (
        <p className="text-sm py-4 opacity-60">Nessuna voce</p>
      )}
    </div>
  );
}
